import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { SparklesIcon, LinkIcon } from '@heroicons/react/24/outline';

const sanitizeAndFormatHtml = (content) => {
    if (!content || typeof content !== 'string') return '';
    let html = content;
    html = html.replace(/<a\s+[^>]*?>/gi, (tag) => {
        const attrMatch = tag.match(/(?:["']?(?:link|href)\s*=\s*\\?["']?\s*)([^"'\\>]+?)(?:\s*\\?["']?\s*)(?:>|\s|$)/i);
        if (!attrMatch) return tag;
        const rawUrl = attrMatch[1].trim().replace(/^["']|["']$/g, '');
        const targetMatch = tag.match(/target\s*=\s*["']?([^"'>\s]+)["']?/i);
        const targetAttr = targetMatch ? ` target="${targetMatch[1]}"` : '';
        return `<a href="${rawUrl}"${targetAttr}>`;
    });
    if (!/<(?:br|p|div)\b/i.test(html)) {
        html = html.replace(/\r?\n/g, '<br />');
    }
    return html;
};

const DSAPartnerProgramCTABannerPage = () => {
    const [settings, setSettings] = useState({
        title: 'Ready to Start Your Partnership Journey?',
        description: 'Join hundreds of successful partners earning with Aham Housing Finance and start building your income today',
        button_text: 'Get Started Today',
        button_link: '#',
        bottom_text: 'Ready to partner with Aham? Complete your <a href="https://ahamhfc.com/home-loan-dsa-registration-online">Home Loan DSA registration online</a> to get started.'
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Link inserter state
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkText, setLinkText] = useState('');
    const [linkUrl, setLinkUrl] = useState('');

    const handleInsertLink = () => {
        if (!linkUrl.trim()) {
            toast.error('Please enter a valid URL (e.g. https://ahamhfc.com/home-loan-dsa-registration-online)');
            return;
        }
        const label = linkText.trim() || 'Home Loan DSA registration online';
        const cleanUrl = linkUrl.trim();
        const tag = `<a href="${cleanUrl}">${label}</a>`;
        const currentValue = settings.bottom_text || '';
        const updatedValue = currentValue ? `${currentValue} ${tag}` : tag;
        setSettings(prev => ({ ...prev, bottom_text: updatedValue }));
        toast.success(`Inserted link: ${label}`);
        setShowLinkModal(false);
        setLinkText('');
        setLinkUrl('');
    };

    const LOAN_TYPE = 'dsa_partner_program';

    // Fetch data
    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/admin/cta-banner/settings/${LOAN_TYPE}`);
            if (response.data.data) {
                setSettings(prev => ({
                    ...prev,
                    ...response.data.data,
                    bottom_text: response.data.data.bottom_text || 'Ready to partner with Aham? Complete your <a href="https://ahamhfc.com/home-loan-dsa-registration-online">Home Loan DSA registration online</a> to get started.'
                }));
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load CTA banner settings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handle settings update
    const handleSave = async () => {
        try {
            setSaving(true);
            await axios.put(`/api/admin/cta-banner/settings/${LOAN_TYPE}`, settings);
            toast.success('CTA banner settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-slate-800">CTA Banner</h1>
                <p className="text-slate-500 mt-1">Manage the call-to-action banner for Partner Program</p>
            </div>

            {/* Settings Form */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
                <h2 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                    <SparklesIcon className="h-5 w-5 text-primary-600" />
                    CTA Banner Settings
                </h2>

                {loading ? (
                    <div className="space-y-4">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="h-12 bg-slate-100 rounded-xl animate-pulse"></div>
                        ))}
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Title */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Title
                            </label>
                            <input
                                type="text"
                                value={settings.title}
                                onChange={(e) => setSettings({ ...settings, title: e.target.value })}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="e.g., Ready to Start Your Partnership Journey?"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Description
                            </label>
                            <textarea
                                rows="3"
                                value={settings.description}
                                onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="Enter description..."
                            />
                        </div>

                        {/* Bottom Text / Registration Paragraph with Link Inserter */}
                        <div className="border-t border-slate-100 pt-6">
                            <div className="flex items-center justify-between mb-2">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700">
                                        Registration Notice Paragraph (Under Description)
                                    </label>
                                    <p className="text-xs text-slate-500">
                                        Appears immediately below the existing description and above the CTA button.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowLinkModal(!showLinkModal);
                                        setLinkText('');
                                        setLinkUrl('');
                                    }}
                                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors border border-indigo-200"
                                >
                                    <LinkIcon className="w-3.5 h-3.5" />
                                    <span>{showLinkModal ? 'Cancel Link' : '+ Insert Link'}</span>
                                </button>
                            </div>

                            {showLinkModal && (
                                <div className="bg-indigo-50/80 border border-indigo-200 rounded-xl p-3 mb-3 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-bold text-indigo-900">Insert Link into Paragraph</span>
                                        <span className="text-[11px] text-indigo-600">e.g. /home-loan-dsa-registration-online or https://...</span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Link Text</label>
                                            <input
                                                type="text"
                                                value={linkText}
                                                onChange={e => setLinkText(e.target.value)}
                                                placeholder="e.g. Home Loan DSA registration online"
                                                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Target URL</label>
                                            <input
                                                type="text"
                                                value={linkUrl}
                                                onChange={e => setLinkUrl(e.target.value)}
                                                placeholder="e.g. https://ahamhfc.com/home-loan-dsa-registration-online"
                                                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex justify-end gap-2 pt-1">
                                        <button
                                            type="button"
                                            onClick={() => setShowLinkModal(false)}
                                            className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-lg"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleInsertLink}
                                            className="px-3 py-1 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm"
                                        >
                                            Insert &lt;a&gt; Tag
                                        </button>
                                    </div>
                                </div>
                            )}

                            <textarea
                                rows="3"
                                value={settings.bottom_text || ''}
                                onChange={(e) => setSettings({ ...settings, bottom_text: e.target.value })}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-normal"
                                placeholder="Ready to partner with Aham? Complete your <a href=&quot;https://ahamhfc.com/home-loan-dsa-registration-online&quot;>Home Loan DSA registration online</a> to get started."
                            />
                            <p className="mt-1 text-[11px] text-slate-500">
                                Tip: HTML anchor tags and line breaks are fully supported.
                            </p>

                            {/* Live Preview */}
                            {settings.bottom_text && (
                                <div className="mt-3 bg-[#1e1b4b] p-5 rounded-2xl border border-indigo-900/30 text-white space-y-3 shadow-md">
                                    <div className="flex items-center justify-between border-b border-white/15 pb-2.5">
                                        <div className="flex items-center gap-2">
                                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                            <span className="text-xs font-bold uppercase tracking-wider text-yellow-400">PARAGRAPH LIVE PREVIEW</span>
                                        </div>
                                        <span className="text-xs text-gray-300">Live preview with clickable links</span>
                                    </div>
                                    <div
                                        className="text-gray-100 text-sm md:text-base leading-relaxed [&_a]:text-yellow-400 [&_a]:underline hover:[&_a]:text-yellow-300 [&_a]:font-semibold [&_a]:transition-colors"
                                        dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(settings.bottom_text) }}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Button Text */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Button Text
                                </label>
                                <input
                                    type="text"
                                    value={settings.button_text}
                                    onChange={(e) => setSettings({ ...settings, button_text: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    placeholder="e.g., Get Started Today"
                                />
                            </div>

                            {/* Button Link */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Button Link
                                </label>
                                <input
                                    type="text"
                                    value={settings.button_link}
                                    onChange={(e) => setSettings({ ...settings, button_link: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    placeholder="e.g., # or /contact"
                                />
                                <p className="text-xs text-slate-500 mt-1">Use # to open the enquiry modal</p>
                            </div>
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end pt-4">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-lg"
                            >
                                {saving ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DSAPartnerProgramCTABannerPage;
